import type Harvest from "@/models/harvest";
import HarvestRepository from "@/repositories/harvestRepository";
import type { HarvestCreatePayload, HarvestUpdatePayload } from "@/types";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useAuthStore } from "./auth";

// Recorded pickings (the harvest journal). Loaded on demand with optional filters
// (crop / date window) rather than eagerly on login.
export const useHarvestsStore = defineStore("harvests", () => {
  const harvests = ref<Harvest[]>([]);
  const areHarvestsLoaded = ref(false);

  // crop id → total harvested kg, for quick lookup of the currently loaded set.
  const harvestedKgByCrop = computed<Record<number, number>>(() => {
    const totals: Record<number, number> = {};
    for (const h of harvests.value) totals[h.crop] = (totals[h.crop] ?? 0) + h.quantityKg;
    return totals;
  });

  async function load(params?: { crop?: number; start_date?: string; end_date?: string }) {
    const auth = useAuthStore();
    if (!auth.isAuthenticated) return;
    harvests.value = await HarvestRepository.getAll(params);
    areHarvestsLoaded.value = true;
  }

  async function create(payload: HarvestCreatePayload): Promise<Harvest> {
    const harvest = await HarvestRepository.create(payload);
    // Keep the most-recent-first ordering the API uses.
    harvests.value = [harvest, ...harvests.value];
    return harvest;
  }

  async function update(id: number, payload: HarvestUpdatePayload): Promise<Harvest> {
    const harvest = await HarvestRepository.update(id, payload);
    const index = harvests.value.findIndex((h) => h.id === id);
    if (index !== -1) harvests.value[index] = harvest;
    return harvest;
  }

  async function remove(id: number) {
    await HarvestRepository.delete(id);
    harvests.value = harvests.value.filter((h) => h.id !== id);
  }

  function resetHarvests() {
    harvests.value = [];
    areHarvestsLoaded.value = false;
  }

  return { harvests, areHarvestsLoaded, harvestedKgByCrop, load, create, update, remove, resetHarvests };
});
